import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, InputNumber, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CreateBtn from '@/components/app/CreateBtn';
import BooleanTag from '@/components/app/BooleanTag';
import TicketConditionInput from '@/components/FormItems/TicketConditionInput';
import { TariffParameterDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<TariffParameterDto>[] = [
  {
    title: 'ticketModificationSetting_TicketCondition',
    dataIndex: 'ticketConditionName',
  },
  {
    title: 'ticketModificationSetting_AllowToModifyTicket',
    dataIndex: 'allowToModifyTicket',
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'ticketModificationSetting_IsPercentageBase',
    dataIndex: 'isPercentageBase',
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'ticketModificationSetting_Amount',
    dataIndex: 'amount',
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'ticketConditionId', label: 'ticketModificationSetting_TicketCondition', customElm: <TicketConditionInput /> },
];

const TicketModificationSettingTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const createEntity = values => {
    setLoading(true);
    apiSdk.TicketModificationSettingService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.TicketModificationSettingService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/bookings/ticket-modification-settings/get-ticket-modification-settings"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/bookings/ticket-modification-settings/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
      ></ZTable>
      {formVisible ? (
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('ticketModificationSetting_Create') : t('ticketModificationSetting_Update')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item
                  label={t('ticketModificationSetting_TicketCondition')}
                  name="ticketConditionId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <TicketConditionInput />
                </Form.Item>
                <Form.Item colon={false} label="    " name="allowToModifyTicket" valuePropName="checked">
                  <Checkbox>{t('ticketModificationSetting_AllowToModifyTicket')}</Checkbox>
                </Form.Item>
                <Form.Item colon={false} label="    " name="isPercentageBase" valuePropName="checked">
                  <Checkbox>{t('ticketModificationSetting_IsPercentageBase')}</Checkbox>
                </Form.Item>
                <Form.Item label={t('ticketModificationSetting_Amount')} name="amount" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <InputNumber className="w-100" min={0} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default TicketModificationSettingTab;
